import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  PATHWAY_DAY_LABEL,
  PathwayUploadDaySummary,
  PathwayUploadPreviewData,
  PathwayUploadReportRow,
  PathwayStopChange,
  pathwayReasonLabel,
} from "../../types/pathway-upload.type";
import { downloadSkippedRowsCsv } from "./reportCsv";
import { toExcelReference } from "./excelReference";

const CHANGE_LABEL: Record<PathwayStopChange, string> = {
  UNCHANGED: "–",
  ADDED: "เพิ่มใหม่",
  REMOVED: "ถูกนำออก",
  MOVED: "ย้ายลำดับ",
};

const CHANGE_COLOR: Record<
  PathwayStopChange,
  "default" | "success" | "error" | "warning"
> = {
  UNCHANGED: "default",
  ADDED: "success",
  REMOVED: "error",
  MOVED: "warning",
};

const cardSx = { boxShadow: "none", borderRadius: "12px" } as const;

interface SummaryStatProps {
  label: string;
  value: string | number;
}

const SummaryStat = ({ label, value }: SummaryStatProps) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6">{value}</Typography>
  </Box>
);

interface DayRowProps {
  day: PathwayUploadDaySummary;
}

/** หนึ่งแถวของตาราง สาย × วัน กางดูรายละเอียดร้านทีละวันได้ */
const DayRow = ({ day }: DayRowProps) => {
  const [open, setOpen] = useState(false);
  const unchanged = day.added === 0 && day.removed === 0 && day.moved === 0;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: "pointer" }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <TableCell>{day.route_code}</TableCell>
        <TableCell>{PATHWAY_DAY_LABEL[day.pathway_day]}</TableCell>
        <TableCell align="right">{day.current_count}</TableCell>
        <TableCell align="right">
          <strong>{day.new_count}</strong>
        </TableCell>
        <TableCell align="right">{day.added || "–"}</TableCell>
        <TableCell align="right">{day.removed || "–"}</TableCell>
        <TableCell align="right">{day.moved || "–"}</TableCell>
        <TableCell align="center">
          {unchanged ? (
            <Chip size="small" label="ไม่เปลี่ยนแปลง" />
          ) : (
            <Chip size="small" color="primary" label="มีการเปลี่ยนแปลง" />
          )}
        </TableCell>
        <TableCell align="center" padding="none">
          {open ? <ExpandLess /> : <ExpandMore />}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={{ p: 0, border: 0 }} colSpan={9}>
          <Collapse in={open} unmountOnExit timeout="auto">
            <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ลำดับใหม่</TableCell>
                    <TableCell>ลำดับเดิม</TableCell>
                    <TableCell>รหัสร้าน</TableCell>
                    <TableCell>ชื่อร้าน (จากระบบ)</TableCell>
                    <TableCell>การเปลี่ยนแปลง</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {day.stops.map((stop, index) => (
                    <TableRow key={`${stop.customer_code}-${index}`}>
                      <TableCell>{stop.stop_no ?? "–"}</TableCell>
                      <TableCell>{stop.previous_stop_no ?? "–"}</TableCell>
                      <TableCell>{stop.customer_code || "–"}</TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          gap={0.5}
                          alignItems="center"
                          component="span"
                        >
                          {stop.customer_name ?? "–"}
                          {stop.name_mismatch && (
                            <Tooltip
                              title={`ในไฟล์เขียนว่า "${stop.excel_name ?? ""}" — ตรวจสอบว่าพิมพ์รหัสถูกหรือไม่`}
                            >
                              <WarningAmberIcon
                                color="warning"
                                fontSize="small"
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={CHANGE_COLOR[stop.change]}
                          label={CHANGE_LABEL[stop.change]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// วิธีใช้ตำแหน่งในไฟล์ อธิบายครั้งเดียวที่หัวคอลัมน์ แทนที่จะซ้ำทุกแถว
const LOCATION_HELP = (
  <>
    <strong>วิธีหาแถวนี้ในไฟล์ Excel</strong>
    <br />
    1. เปิดไฟล์แล้วคลิกที่ชื่อชีตด้านล่าง (เช่น ผ.4)
    <br />
    2. กด <strong>Ctrl + G</strong>
    <br />
    3. วางข้อความที่คัดลอกไว้ในช่อง Reference แล้วกด Enter
    <br />
    Excel จะกระโดดไปที่เซลล์นั้นทันที — คลิกที่ตำแหน่งในตารางเพื่อคัดลอกได้เลย
  </>
);

interface LocationCellProps {
  location: string;
}

const LocationCell = ({ location }: LocationCellProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(toExcelReference(location));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // เบราว์เซอร์ไม่อนุญาต clipboard — ผู้ใช้ยังพิมพ์ตามที่เห็นได้
    }
  };

  return (
    <TableCell sx={{ whiteSpace: "nowrap" }}>
      <Tooltip
        title={
          copied
            ? "คัดลอกแล้ว — กด Ctrl + G ในไฟล์ Excel แล้ววาง"
            : `คลิกเพื่อคัดลอก ${toExcelReference(location)} ไปวางใน Ctrl + G`
        }
      >
        <Box
          component="span"
          onClick={handleCopy}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {location}
          {copied ? (
            <DoneIcon color="success" sx={{ fontSize: 14 }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 14, opacity: 0.5 }} />
          )}
        </Box>
      </Tooltip>
    </TableCell>
  );
};

interface ReportTableProps {
  title: string;
  rows: PathwayUploadReportRow[];
  onDownload?: () => void;
}

const ReportTable = ({ title, rows, onDownload }: ReportTableProps) => (
  <Card sx={cardSx}>
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="subtitle1">
          {title} ({rows.length})
        </Typography>
        {onDownload && (
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={onDownload}
          >
            ดาวน์โหลด CSV
          </Button>
        )}
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <Stack
                direction="row"
                gap={0.5}
                alignItems="center"
                component="span"
              >
                ตำแหน่งในไฟล์
                <Tooltip title={LOCATION_HELP}>
                  <HelpOutlineIcon sx={{ fontSize: 15, opacity: 0.6 }} />
                </Tooltip>
              </Stack>
            </TableCell>
            <TableCell>สาย</TableCell>
            <TableCell>วัน</TableCell>
            <TableCell>ข้อความในเซลล์</TableCell>
            <TableCell>สาเหตุ</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={`${row.location}-${index}`}>
              <LocationCell location={row.location} />
              <TableCell>{row.route_code ?? "–"}</TableCell>
              <TableCell>
                {row.pathway_day ? PATHWAY_DAY_LABEL[row.pathway_day] : "–"}
              </TableCell>
              <TableCell>{row.cell_text}</TableCell>
              <TableCell>{pathwayReasonLabel(row.reason)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

interface PreviewReportProps {
  preview: PathwayUploadPreviewData;
}

/**
 * รายงานผลตรวจไฟล์ก่อนยืนยัน — ยังไม่มีอะไรถูกเขียนลงฐานข้อมูลในขั้นนี้
 * ชื่อร้านทุกจุดมาจากฐานข้อมูล ส่วนชื่อในไฟล์แสดงเป็น tooltip เฉพาะตอนไม่ตรงกัน
 */
const PreviewReport = ({ preview }: PreviewReportProps) => {
  const { summary } = preview;

  return (
    <Stack gap={2}>
      <Card sx={cardSx}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Typography variant="subtitle1" mb={0.5}>
            {preview.file_name}
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" gap={4} flexWrap="wrap">
            <SummaryStat label="สายในไฟล์" value={summary.routes} />
            <SummaryStat label="แถวที่อ่านได้" value={summary.rows_read} />
            <SummaryStat
              label="ร้านที่จะบันทึก"
              value={summary.rows_to_apply}
            />
            <SummaryStat label="แถวที่ข้าม" value={summary.rows_skipped} />
            <SummaryStat label="แถวที่ต้องดูซ้ำ" value={summary.rows_warned} />
          </Stack>
        </CardContent>
      </Card>

      {summary.affects_today && (
        <Alert severity="warning">
          ไฟล์นี้แก้แผนของ&nbsp;
          <strong>วัน{PATHWAY_DAY_LABEL[summary.today_pathway_day]}</strong>
          &nbsp;ซึ่งเป็นวันนี้ — เมื่อยืนยันแล้ว
          พนักงานขายจะเห็นแผนใหม่ทันที
        </Alert>
      )}

      <Card sx={cardSx}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Typography variant="subtitle1" mb={1}>
            สรุปรายสาย × วัน (กดที่แถวเพื่อดูรายละเอียด)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>สาย</TableCell>
                <TableCell>วัน</TableCell>
                <TableCell align="right">ร้านเดิม</TableCell>
                <TableCell align="right">ร้านใหม่</TableCell>
                <TableCell align="right">เพิ่ม</TableCell>
                <TableCell align="right">ลบ</TableCell>
                <TableCell align="right">สลับลำดับ</TableCell>
                <TableCell align="center">สถานะ</TableCell>
                <TableCell padding="none" />
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.days.map((day) => (
                <DayRow
                  key={`${day.route_uuid}-${day.pathway_day}`}
                  day={day}
                />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {preview.skipped.length > 0 && (
        <ReportTable
          title="แถวที่ระบบข้าม (จะไม่ถูกบันทึก)"
          rows={preview.skipped}
          onDownload={() =>
            downloadSkippedRowsCsv(
              preview.skipped,
              `pathway-skipped-${preview.upload_id.slice(0, 8)}.csv`,
            )
          }
        />
      )}

      {preview.warnings.length > 0 && (
        <ReportTable
          title="แถวที่ไม่มีลำดับ — ระบบต่อท้ายให้ (จะถูกบันทึก)"
          rows={preview.warnings}
        />
      )}
    </Stack>
  );
};

export default PreviewReport;
